import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import FlagIcon from './FlagIcon';
import {
  getFlagCode,
  VOICE_THEME as t,
  uniqueLanguages,
  translate,
  LANGUAGE_PT,
  ACCENT_PT,
} from '../../utils/voiceMetadata';

/**
 * LanguageListPopover — popover read-only que lista os idiomas suportados de
 * uma voz (bandeira + nome PT + sotaque). Posicionado próximo a um elemento
 * âncora via DOMRect. Renderizado em portal pra ficar acima de tudo.
 *
 * Substitui o `title` HTML nativo do "+N idiomas" (que tem visual de
 * "Windows antigo").
 *
 * Props:
 *   - voice: precisa ter voice_info.verified_languages
 *   - anchorRect: DOMRect (do getBoundingClientRect do elemento clicado)
 *   - onClose: chamado em ESC ou click fora
 */
export default function LanguageListPopover({ voice, anchorRect, onClose }) {
  // Cores via CSS vars (VOICE_THEME) — adaptam ao tema sem useTheme
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(null);

  const info = voice && voice.voice_info;
  const verified = info && Array.isArray(info.verified_languages) ? info.verified_languages : [];
  const entries = useMemo(() => uniqueLanguages(verified), [verified]);

  // Calcular posição: prefere abrir DIREITA do âncora. Se sair da viewport,
  // tenta esquerda; se ainda passar, alinha no topo da viewport.
  useEffect(() => {
    if (!anchorRect) return;
    const VIEWPORT_PAD = 8;
    const POPOVER_WIDTH = 280;
    const POPOVER_MAX_HEIGHT = 400;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    // Horizontal: tentar direita
    let left = anchorRect.right + 8;
    if (left + POPOVER_WIDTH + VIEWPORT_PAD > viewportW) {
      // Tenta esquerda
      left = anchorRect.left - POPOVER_WIDTH - 8;
      if (left < VIEWPORT_PAD) {
        // Centraliza horizontalmente sobre o âncora
        left = Math.max(VIEWPORT_PAD, Math.min(viewportW - POPOVER_WIDTH - VIEWPORT_PAD, anchorRect.left));
      }
    }

    // Vertical: alinhar pelo topo do âncora, mas cuidar pra não sair embaixo
    let top = anchorRect.top;
    if (top + POPOVER_MAX_HEIGHT + VIEWPORT_PAD > viewportH) {
      top = Math.max(VIEWPORT_PAD, viewportH - POPOVER_MAX_HEIGHT - VIEWPORT_PAD);
    }

    setPosition({ top, left, width: POPOVER_WIDTH, maxHeight: POPOVER_MAX_HEIGHT });
  }, [anchorRect]);

  // ESC + click fora fecham
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    const onMouseDown = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose && onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [onClose]);

  if (!anchorRect || !position || entries.length === 0) return null;

  const primaryLang = (info && info.language || '').toLowerCase();
  const primaryAcc = (info && info.accent || '').toLowerCase();

  return createPortal(
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
        maxHeight: `${position.maxHeight}px`,
        background: t.bg,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        border: `1px solid ${t.border}`,
        borderRadius: '10px',
        boxShadow: t.shadow,
        zIndex: 1300,
        padding: '8px 4px',
        fontFamily: 'var(--dp-font)',
        animation: 'agFadeInUp 0.16s ease-out',
        overflowY: 'auto',
        color: t.text,
      }}
      className="ag-scrollbar"
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{
        padding: '4px 10px 6px',
        fontSize: '9.5px',
        fontWeight: 700,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        color: t.textTertiary,
      }}>Idiomas suportados ({entries.length})</div>
      {entries.map((entry, idx) => {
        const lang = (entry.language || '').toLowerCase();
        const acc = (entry.accent || '').toLowerCase();
        const isPrimary = lang === primaryLang && acc === primaryAcc;
        return (
          <div
            key={`${lang}|${acc}|${idx}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 10px',
              fontSize: '11.5px',
              color: t.text,
              borderRadius: '6px',
            }}
          >
            <FlagIcon code={getFlagCode(entry.language, entry.accent, entry.locale)} size="14px" rounded />
            <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {translate(entry.language, LANGUAGE_PT)}
              {entry.accent && entry.accent.toLowerCase() !== 'standard' && (
                <span style={{ color: t.textTertiary, marginLeft: 4 }}>
                  ({translate(entry.accent, ACCENT_PT)})
                </span>
              )}
            </span>
            {isPrimary && (
              <span style={{
                fontSize: '8.5px',
                fontWeight: 700,
                color: 'var(--dp-blue-500)',
                background: 'rgba(13,174,255,0.12)',
                padding: '1px 6px',
                borderRadius: '9999px',
                letterSpacing: '0.4px',
              }}>PRIMÁRIO</span>
            )}
          </div>
        );
      })}
    </div>,
    document.body,
  );
}
